import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { PolicyConfigDecisionAttributeFieldResultTypes } from "@enums/policy-config-decision-attribute-field-result-type";
import { AxnAttributeType, AxnAttributeTypeField, AxnCompany } from "@idw-types/data/reference-data/axn";
import { AvailableAssertionStatus, DeployedPolicyComponent, SimpleDeployedPolicyComponentCondition } from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfigurationAttribute, FormattedRpServiceConfigurationPolicyConfigDecisionCondition, FormattedRpServiceConfigurationServiceOffering, FormattedRpServiceConfigurationServiceOfferingAssertion } from "@idw-types/data/rp-service-configuration";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { PolicyDecisionConditionTableRowData } from "@idw-types/common/policy-decision-condition-table-row-data";
import { sortStrings } from "@utils/string-util";
import { IdwFormLabel } from "../../idw-form-label/idw-form-label";
import { ToggleFormGroup } from "../../toggle-form-group/toggle-form-group";
import { PolicyDecisionConditionsTableContainer } from "../policy-decision-conditions-table-container/policy-decision-conditions-table-container";
import { PolicyDecisionConditionComponentContainer } from "./policy-decision-condition-component-container/policy-decision-condition-component-container";
import { PolicyDecisionConditionAttributeFieldContainer } from "./policy-decision-condition-attribute-field-container/policy-decision-condition-attribute-field-container";
import { AddPolicyComponentModal } from "@containers/modals/add-policy-component-modal/add-policy-component-modal";
import { ModalTypes } from "@enums/modal-types";
import { useModalContext } from "@context/use-modal-context";
import { PolicyComponent } from "@idw-types/data/reference-data/generic/policy-component";
import { getAttributeFieldCategory } from "@utils/policies/policy-util";
import styles from "./policy-decision-condition-container.less";
import { AttributeClassificationTypeIds } from "@enums/attribute-classification-types";
import { AttributeTypes } from "@iddataweb/idw-common-ui/enums";
import { getSessionState } from "@redux/selectors/root-selectors";

type PolicyDecisionConditionContainerProps = {
    isDisabled: boolean;
    condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition;
    allDeployedPolicyComponents: Array<DeployedPolicyComponent>;
    unselectedDeployedPolicyComponents: Array<DeployedPolicyComponent>;
    axnAttributeTypes: Array<AxnAttributeType>;
    selectedRpServiceConfigurationAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    allAxnAttributeTypeFields: Array<AxnAttributeTypeField>;
    unselectedAxnAttributeTypeFields: Array<AxnAttributeTypeField>;
    unselectedAxnAcquiredAttributeTypeFields: Array<AxnAttributeTypeField>;
    availableDeployedPolicyComponents: Array<DeployedPolicyComponent>;
    deployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>;
    assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    availableAssertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAcquiredAttributeTypeFields: Array<AxnAttributeTypeField>;
    showAllComponents: boolean;
    axnCompanies: Array<AxnCompany>;
    serviceOrganizationID: string;
    acquiredAttributes: Array<AxnAcquiredAttribute>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    setShowAllComponents: (showAll: boolean) => void;
    updateCondition: (condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition) => void;
    removeCondition: (condition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition) => void;
};

export const PolicyDecisionConditionContainer = (props: PolicyDecisionConditionContainerProps) => {
    const {
        isDisabled,
        condition,
        allDeployedPolicyComponents,
        allAxnAttributeTypeFields,
        axnAttributeTypes,
        selectedRpServiceConfigurationAttributes,
        unselectedDeployedPolicyComponents,
        availableDeployedPolicyComponents,
        deployedPolicyComponentConditions,
        assertions,
        availableAssertions,
        serviceOfferings,
        axnAttributeTypeFields,
        axnAcquiredAttributeTypeFields,
        unselectedAxnAttributeTypeFields,
        unselectedAxnAcquiredAttributeTypeFields,
        showAllComponents,
        axnCompanies,
        serviceOrganizationID,
        acquiredAttributes,
        axnAvailableAssertionStatuses,
        setShowAllComponents,
        updateCondition,
        removeCondition
    } = props;

    const { session } = useSelector(getSessionState);


    const [showDeployedPolicyComponentsTable, setShowDeployedPolicyComponentsTable] = useState(!isDisabled && !condition.component && !condition.attributeField);
    const modalContext = useModalContext();

    const handleTableRowClick = (policyDecisionConditionTableRow: PolicyDecisionConditionTableRowData) => {
        updateCondition({
            assertionID: null,
            attributeField: policyDecisionConditionTableRow.attributeField,
            component: policyDecisionConditionTableRow.component,
            description: policyDecisionConditionTableRow.description,
            id: null,
            policyConditionOperator: PolicyConfigDecisionAttributeFieldResultTypes.EQUAL_TO,
            result: null,
            serviceOfferingID: null,
            values: policyDecisionConditionTableRow.defaultValue,
            attributeFieldCategory: getAttributeFieldCategory(policyDecisionConditionTableRow.type),
            attributeField2Category: null,
            attributeField2ID: null,
            step: null,
            step2: null,
            uuid: condition.uuid
        });
        setShowDeployedPolicyComponentsTable(false);
    };

    const setPolicyComponent = (policyComponent: PolicyComponent) => {
        updateCondition({
            assertionID: null,
            attributeField: null,
            component: policyComponent,
            description: policyComponent.description,
            id: null,
            policyConditionOperator: PolicyConfigDecisionAttributeFieldResultTypes.EQUAL_TO,
            result: null,
            serviceOfferingID: null,
            values: policyComponent.defaultValue,
            attributeFieldCategory: null,
            attributeField2Category: null,
            attributeField2ID: null,
            step: null,
            step2: null,
            uuid: condition.uuid
        });

        setShowDeployedPolicyComponentsTable(false);
    }

    const openCreateComponentModal = () => {
        const renderAddPolicyComponentModal = (onModalClose: () => void) => {
            const userInputAttributeTypes = selectedRpServiceConfigurationAttributes.map(aa => ({
                attributeClassificationID: aa.attributeClassification?.id as AttributeClassificationTypeIds,
                display: aa.display,
                id: aa.attributeType?.id,
                name: aa.name as AttributeTypes,
                templateName: aa.templateName as AttributeTypes,
                verifiable: aa.verifiable
            }));

            return (
                <AddPolicyComponentModal
                    axnCompanies={axnCompanies}
                    allPolicyComponents={allDeployedPolicyComponents}
                    policyComponents={availableDeployedPolicyComponents.filter(pc => !pc.workflowID && (pc.organizationID === serviceOrganizationID || !pc.organizationID))} // include global components that are not in workflow
                    policyComponentConditions={deployedPolicyComponentConditions}
                    assertions={assertions}
                    availableAssertions={availableAssertions}
                    serviceOfferings={serviceOfferings}
                    allAxnAttributeTypes={axnAttributeTypes}
                    axnAttributeTypes={userInputAttributeTypes}
                    allAxnAttributeTypeFields={allAxnAttributeTypeFields}
                    axnAttributeTypeFields={axnAttributeTypeFields}
                    axnAcquiredAttributeTypeFields={axnAcquiredAttributeTypeFields}
                    acquiredAttributes={acquiredAttributes}
                    axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                    updateCommonReferenceData={true}
                    onModalClose={onModalClose}
                    setPolicyComponent={setPolicyComponent}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddPolicyComponentModal,
            modalType: ModalTypes.AddPolicyComponent
        });
    }

    const availableTableRows = useMemo(() => {
        const tableRows: Array<PolicyDecisionConditionTableRowData> = [];
        unselectedDeployedPolicyComponents.forEach(component => {
            tableRows.push({
                name: component.name,
                description: component.description,
                type: "component",
                component: component,
                attributeField: null,
                defaultValue: component.defaultValue || "true",
                category: component.category || resources.common.notFound
            });
        });

        unselectedAxnAttributeTypeFields.forEach(atf => {
            tableRows.push({
                name: atf.label,
                description: atf.description,
                type: "attribute",
                component: null,
                attributeField: atf,
                defaultValue: "",
                category: resources.common.notFound
            });
        });

        unselectedAxnAcquiredAttributeTypeFields.forEach(atf => {
            tableRows.push({
                name: atf.label,
                description: atf.description,
                type: "acquiredAttribute",
                component: null,
                attributeField: atf,
                defaultValue: "",
                category: resources.common.notFound
            });
        });

        return tableRows.sort((a, b) => sortStrings(a.name, b.name));
    }, [
        unselectedDeployedPolicyComponents,
        unselectedAxnAttributeTypeFields,
        unselectedAxnAcquiredAttributeTypeFields
    ]);

    return (
        <div className={styles.policyDecisionConditionContainer}>
            {!condition.component && !condition.attributeField && (
                <div className={styles.headerContainer}>
                    <IdwFormLabel
                        isRequired={false}
                        label={"Select a rule condition from the table below"}
                        labelWidth={LabelWidthTypes.EXTRA_SMALL}
                    />
                    <ToggleFormGroup
                        controlId={`policy-decision-condition-${condition.uuid}-show-all-policy-components-toggle`}
                        className={styles.toggleFormGroup}
                        disabled={isDisabled}
                        isRequired={false}
                        label={"Show All Components"}
                        onChange={() => setShowAllComponents(!showAllComponents)}
                        value={`${showAllComponents}`}
                        checked={showAllComponents}
                        checkedIcon={false}
                        uncheckedIcon={false}
                    />

                    {session.userPermissions.canCreatePolicyComponents && (
                        <Button className={styles.createComponentButton} onClick={openCreateComponentModal}>{"Create a new component"}</Button>
                    )}
                    <div id={`policy-decision-condition-${condition.uuid}-remove-condition`} className={styles.removeIconContainer} onClick={() => removeCondition(condition)}>
                        <FontAwesomeIcon className={styles.removeIcon} icon={faTimesCircle} />
                    </div>
                </div>
            )}
            {condition.component && (
                <PolicyDecisionConditionComponentContainer
                    isDisabled={isDisabled}
                    condition={condition}
                    deployedPolicyComponents={allDeployedPolicyComponents}
                    deployedPolicyComponentConditions={deployedPolicyComponentConditions}
                    axnAttributeTypeFields={allAxnAttributeTypeFields}
                    serviceOfferings={serviceOfferings}
                    updateCondition={updateCondition}
                    removeCondition={removeCondition}
                />
            )}
            {condition.attributeField && (
                <PolicyDecisionConditionAttributeFieldContainer
                    isDisabled={isDisabled}
                    condition={condition}
                    updateCondition={updateCondition}
                    removeCondition={removeCondition}
                />
            )}
            {showDeployedPolicyComponentsTable && (
                <PolicyDecisionConditionsTableContainer
                    policyDecisionConditionTableRows={availableTableRows}
                    onClickTableRow={handleTableRowClick}
                />
            )}
        </div>
    );
};